import { activeSiteConfig } from "@config/site/site-config";
import { ClickAndCollect, CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { IconButton } from "@ui/components/forms/icon-button/IconButton";
import { Input } from "@ui/components/forms/input/Input";
import { InputLeftElement, InputRightElement } from "@ui/components/forms/input/InputElement";
import { InputGroup } from "@ui/components/forms/input/InputGroup";
import { Box } from "@ui/components/layout/box/Box";
import { ECCOIcon } from "@ui/components/media-and-icons/ecco-icon/ECCOIcon";
import { sizes } from "@ui/design-tokens";
import type { ChangeEvent, FormEvent } from "react";
import { useCallback, useContext, useEffect, useState, useRef } from "react";
import { Controller } from "./controller";
import { formatStreet } from "./normalize-store";
import { useRouter } from "next/router";
import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";
import { SEARCH } from "@e2e/helpers/store-locator/constants/common";

export const Search = ({ isClickAndCollect }) => {
    const { getMultiple } = useMicrocopy();
    const {
        storeData,
        setSelectedStoreId,
        setSearchResult,
        setLastQuery,
        validStoreId,
        setIsLoading,
        shouldRestrictCountry,
        setFilterDistance,
        filterDistance,
    } = useContext(Controller);
    const { query: routerQuery } = useRouter();
    const [autocompleteState, setAutocompleteState] = useState<any>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const timeoutRef = useRef<any>(null);
    const autocompleteContainerRef = useRef(null);

    const [query, setQuery] = useState<string>(
        typeof routerQuery.storeName == "string" ? routerQuery.storeName : ""
    );

    const { country } = activeSiteConfig;

    const microcopies = (() => {
        return getMultiple(CommonCMS.page, [
            ClickAndCollect.storeLocatorPlaceholder,
            ClickAndCollect.storeLocatorReset,
        ]);
    })();

    const searchStoreById = (storeId: string) => {
        const store = storeData?.find((store) => store?.storeId === storeId);
        if (store) {
            searchStoresByAddress(`${formatStreet(store)}, ${store.postalCode} ${store.city}`);
        }
    };

    const searchStoresByAddress = (address: string) => {
        setIsLoading(true);
        if (address.trim() === "") {
            setIsLoading(false);
            return;
        }

        const geocoder = new google.maps.Geocoder();

        geocoder
            .geocode({
                address,
                componentRestrictions: shouldRestrictCountry ? { country } : undefined,
            })
            .then((response) => {
                setSelectedStoreId("");
                setSearchResult(response.results);
            })
            .catch((error) => {
                if (error.code === "ZERO_RESULTS") {
                    setSearchResult([]);
                } else {
                    // eslint-disable-next-line
                    Logger.error(
                        ServiceType.WEB,
                        `Error while searching for stores with Google API.`,
                        {
                            tag: LogTag.ERROR,
                            error: {
                                name: error?.name,
                                message: error?.message,
                                stack: error?.stack,
                                ...error,
                            },
                        }
                    );
                }
            })
            .finally(() => {
                // TODO: Refactor; setIsLoading(false) will be first set in map 'idle' event listener (StoreLocatorMap.tsx)
                // this is in case it didn't for some reason
                setTimeout(() => setIsLoading(false), 1500);
                if (filterDistance) setFilterDistance(null);
            });
    };

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        setLastQuery(query);
        if (validStoreId(query)) {
            searchStoreById(query);
        } else {
            searchStoresByAddress(query);
        }
    };

    const autocompleteOptions = {
        componentRestrictions: { country },
        fields: ["address_components", "geometry", "name"],
        strictBounds: false,
        types: ["shoe_store", "clothing_store", "shopping_mall"],
    };

    const generateAddressQuery = (place: any) => {
        const name = place.name;
        const addressComponents = place.address_components;

        const route =
            addressComponents.find((component) => component.types.includes("route"))?.long_name ||
            "";

        const locality =
            addressComponents.find((component) => component.types.includes("locality"))
                ?.long_name || "";

        const political =
            addressComponents.find((component) =>
                component.types.includes("administrative_area_level_1")
            )?.short_name || "";

        return `${name} ${route}, ${locality}, ${political}`;
    };

    useEffect(() => {
        const autocompleteInstance = null;
        const initializeAutocomplete = async () => {
            if (!inputRef.current) return;
            const google = window.google;
            try {
                await google.maps.importLibrary("places");
                const autocompleteInstance = new google.maps.places.Autocomplete(
                    inputRef.current,
                    autocompleteOptions
                );

                setAutocompleteState(autocompleteInstance);
                const handlePlaceChanged = () => {
                    // In this moment we have access to the container of the autocomplete dropdown
                    const place = autocompleteInstance.getPlace();

                    if (place.geometry) {
                        autocompleteContainerRef.current?.replaceChildren();
                        setSearchResult([place] as any);
                        setQuery(generateAddressQuery(place));
                    }
                };
                autocompleteInstance.addListener("place_changed", handlePlaceChanged);
            } catch (error) {
                Logger.error(
                    ServiceType.WEB,
                    `Error while fetching Google Maps library for store locator search.`,
                    {
                        tag: LogTag.ERROR,
                        error: {
                            name: error?.name,
                            message: error?.message,
                            stack: error?.stack,
                            ...error,
                        },
                    }
                );
            }
        };

        initializeAutocomplete();
        return () => {
            if (autocompleteInstance) {
                google.maps.event.clearInstanceListeners(autocompleteInstance);
            }
        };
    }, []);

    useEffect(() => {
        if (autocompleteState !== null) {
            timeoutRef.current = setTimeout(() => {
                const container = document.querySelector(".pac-container");
                if (container) {
                    autocompleteContainerRef.current = container as HTMLElement;
                    // The autocomplete list is placed outside the main DOM element, we need this to override the z-index and place it on top of the Modal on CC Store locator. Otherwise, set z-index= 4
                    !isClickAndCollect &&
                        (autocompleteContainerRef.current.setAttribute(
                            "style",
                            "z-index: 4; display: none"
                        ),
                        autocompleteContainerRef.current.classList.add(
                            "store-locator__autocomplete"
                        ));
                }
            }, 750);
        }

        // Cleanup function
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [autocompleteState]);

    const handleReset = useCallback(() => {
        autocompleteContainerRef.current?.replaceChildren();
        setQuery("");
        setLastQuery("");
        setSelectedStoreId("");
        setSearchResult(undefined);
        // We need to remove the childrens manually after clearing the input, otherwise the list will be still in the DOM, just not visible.
    }, [setLastQuery, setSearchResult, setSelectedStoreId]);

    const handleQuery = (event: ChangeEvent<HTMLInputElement>) => setQuery(event.target.value);

    useEffect(() => {
        typeof routerQuery.storeName == "string" &&
            setTimeout(() => {
                setIsLoading(true);
                searchStoresByAddress(routerQuery.storeName as string);
            }, 1000);
    }, [routerQuery.storeName]);

    return (
        <Box px={{ base: 3, xl: 0 }} pr={{ base: 3, xl: 6 }}>
            <form onSubmit={handleSubmit}>
                <InputGroup>
                    <InputLeftElement
                        position={["absolute"]}
                        w={sizes.icon["4xs"]}
                        h={"full"}
                        zIndex={3}
                    >
                        <ECCOIcon name="search" color="black" />
                    </InputLeftElement>
                    <Input
                        ref={inputRef}
                        data-testid={SEARCH}
                        type="text"
                        name="query"
                        value={query}
                        sx={{
                            px: 10,
                            py: 0,
                            h: ["55px", 10.4],
                            _focusVisible: {
                                outlineColor: "transparent",
                                outline: "none",
                                borderColor: "gray.800  ",
                            },
                            _hover: {
                                outline: "none",
                            },
                            _focus: {
                                outline: "none",
                            },
                            _placeholder: {
                                color: "gray.500",
                            },
                        }}
                        onChange={handleQuery}
                        placeholder={microcopies[ClickAndCollect.storeLocatorPlaceholder]}
                    />
                    <InputRightElement h="10.4">
                        {query && (
                            <IconButton
                                variant="ghost"
                                size={"md"}
                                aria-label={microcopies[ClickAndCollect.storeLocatorReset]}
                                icon={<ECCOIcon name="close" color="black" />}
                                onClick={handleReset}
                                // We need this to prevent the focus outline to be outside the input element
                                sx={{ "&:focus-visible": { outlineOffset: "-2px" } }}
                            />
                        )}
                    </InputRightElement>
                </InputGroup>
            </form>
        </Box>
    );
};
